/**
 * @file WinConfig.ts
 * @role Loads + validates wins.config.json: the win-presentation tunables (how far losing
 *       symbol dimming, tier choreography, and the bet multiples that classify larger wins.
 *       All win "feel" lives here, never inline in code.
 * @owns The validated dim alpha, choreography presets, and win thresholds.
 * @reads Bundled wins.config.json (default) or injected raw config (tests).
 * @neverDoes Render. Decide wins/outcomes (the RGS does). Drive effects (the controller does).
 * @invariants 10 (validate before use).
 * @see docs/CONFIG_GUIDE.md, src/game/wins/WinPresentationController.ts
 */
import { z } from 'zod';
import winsConfigRaw from '@config/wins.config.json';
import { GameError } from '@core/errors/GameError';

export const SymbolAnimationStyleSchema = z.enum([
  'punch',
  'squash',
  'bounce',
  'wiggle',
  'elastic',
]);
export type SymbolAnimationStyle = z.infer<typeof SymbolAnimationStyleSchema>;

const WinTierChoreographySchema = z
  .object({
    anticipationMs: z.number().int().nonnegative(),
    symbolStaggerMs: z.number().int().nonnegative(),
    symbolPunchMs: z.number().int().positive(),
    symbolAnimation: SymbolAnimationStyleSchema,
    peakScale: z.number().min(1).max(1.5),
    compressScale: z.number().min(0.8).max(1),
    rotationDeg: z.number().min(-10).max(10),
    paylineSegmentMs: z.number().int().nonnegative(),
    paylinePulseMs: z.number().int().nonnegative(),
    completionHoldMs: z.number().int().nonnegative(),
    exitMs: z.number().int().positive(),
    amountCountMs: z.number().int().nonnegative(),
    /**
     * Guaranteed readable window before a tap on SPIN may skip this tier's presentation.
     *
     * A jackpot's reveal, hero banner, and first celebration beat all land inside it, so the player
     * always sees what they won. Skip is not removed — issue #105 requires it — only delayed. Zero
     * means immediately skippable, which is right for the small tiers nobody wants to sit through.
     */
    minSkipDelayMs: z.number().int().nonnegative(),
    sustainedSymbolPulse: z.boolean(),
    maxTotalMs: z.number().int().positive(),
  })
  .refine((tier) => tier.minSkipDelayMs < tier.maxTotalMs, {
    // A window at or past the tier's own ceiling would make the presentation unskippable outright,
    // which is a different product decision than "hold it briefly" and should not happen by typo.
    message: 'minSkipDelayMs must be shorter than maxTotalMs, or the tier can never be skipped',
    path: ['minSkipDelayMs'],
  });

const ChoreographySchema = z.object({
  speedMultiplier: z.number().positive(),
  quickSpeedMultiplier: z.number().gt(1),
  tiers: z.object({
    small: WinTierChoreographySchema,
    medium: WinTierChoreographySchema,
    big: WinTierChoreographySchema,
    jackpot: WinTierChoreographySchema,
  }),
});

const WinsConfigSchema = z.object({
  /** Alpha applied to non-winning symbols while a win is presented (0..1). */
  dimAlpha: z.number().min(0).max(1),
  /** totalWin >= bet * this multiple is presented as a medium win. */
  mediumWinThresholdMultiplier: z.number().positive(),
  /** totalWin >= bet * this multiple is presented as a big win. */
  bigWinThresholdMultiplier: z.number().positive(),
  /** totalWin >= bet * this multiple is presented as a jackpot win. */
  jackpotWinThresholdMultiplier: z.number().positive(),
  choreography: ChoreographySchema,
});

export type WinsConfigValues = z.infer<typeof WinsConfigSchema>;
export type WinTierChoreography = z.infer<typeof WinTierChoreographySchema>;
export type WinChoreographyConfig = z.infer<typeof ChoreographySchema>;

export class WinConfig {
  readonly dimAlpha: number;
  readonly mediumWinThresholdMultiplier: number;
  readonly bigWinThresholdMultiplier: number;
  readonly jackpotWinThresholdMultiplier: number;
  readonly choreography: WinChoreographyConfig;

  constructor(raw: unknown = winsConfigRaw) {
    const parsed = WinsConfigSchema.safeParse(raw);
    if (!parsed.success) {
      throw new GameError('CONFIG_LOAD_FAILED', `Invalid wins config: ${parsed.error.message}`, {
        cause: parsed.error,
      });
    }
    this.dimAlpha = parsed.data.dimAlpha;
    this.mediumWinThresholdMultiplier = parsed.data.mediumWinThresholdMultiplier;
    this.bigWinThresholdMultiplier = parsed.data.bigWinThresholdMultiplier;
    this.jackpotWinThresholdMultiplier = parsed.data.jackpotWinThresholdMultiplier;
    this.choreography = parsed.data.choreography;
  }
}
